import { Component, OnInit, OnDestroy, Input } from '@angular/core';
// ionic
import { ModalController } from '@ionic/angular';
import { AlertController } from '@ionic/angular';
import { Events } from '../../helpers/events';


// rxjs
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// services
import { PlatformService } from "../../services/platform.service";
import { OverlayService } from "../../services/overlay.service";
import { ConversationService } from "../../services/conversation.service";
import { PreviewService } from '../../services/preview.service';

// models
import { Appointment } from "../../models/appointment";
import { Event } from "../../models/event";
import { Conversation } from "../../models/conversation";
import { Container } from "../../models/container";
import { Group } from "../../models/group";
import { Keyword } from "../../models/keyword";
import { Participant } from "../../models/participant";
import { ParticipantDetail } from "../../models/participant-detail";
import { User } from "../../models/user";

// extends
import { BasePage } from "../base";

@Component({
    selector: 'preview-attendee-detail',
    templateUrl: '../../components/attendee-detail/attendee-detail.component.html',
    styleUrls: ['../../components/attendee-detail/attendee-detail.component.scss'],
})
export class PreviewAttendeeDetailPage extends BasePage implements OnInit, OnDestroy {

    /**
     * if we should show sidebar
     *
     * @type {boolean}
     */
    public disableSidebar: boolean = true;

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * event
     *
     * @type {Event}
     */
    public event: Event;

    /**
     * user
     *
     * @type {User}
     */
    public user: User = new User;

    /**
     * selected language
     *
     * @type string
     */
    public userLang: string;

    /**
     * show group icon
     *
     * @type {Group}
     */
    public group: Group;

    /**
     * show group icon
     *
     * @type {string}
     */
    public groupIcon: string = '';

    /**
     * show loading
     *
     * @type {boolean}
     */
    public loading: boolean = true;

    /**
     * close modal after appointment request
     *
     * @type {boolean}
     */
    public closeAfterAppointmentRequest: boolean = false;

    /**
     * profile of attendee which is presented
     *
     * @type {Participant}
     */
    public attendee: Participant;

    /**
     * if we want to use actual profile from db or profile from input
     *
     * @type {boolean}
     */
    public isPreview: boolean = true;

    /**
     * if we want to use hide header
     *
     * @type {boolean}
     */
    public hideHeader: boolean = false;

    /**
     * decide if we will show appointment request
     *
     * @type {Participant}
     */
    public appointmentRequest: boolean = false;

    public source: string = '';

    /**
     * Show specific detail for participant
     *
     * @type {ParticipantDetail}
     */
    public detail: ParticipantDetail;

    /**
     * like loading status
     *
     * @type {boolean}
     */
    public likeLoading: boolean = false;

    /**
     * bookmark loading status
     *
     * @type {boolean}
     */
    public bookmarkLoading: boolean = false;

    /**
     * decide to show particiapnt image
     *
     * @type {boolean}
     */
    public showImage: boolean = false;

    public appointments: Appointment[] = [];
    public unprocessedAppointments: Appointment[] = [];
    public processedAppointments: Appointment[] = [];

    /**
     * Text with reason which will be sended to other participant
     *
     * @type {string}
     */
    public cancelReason: string = '';

    public containers: Container[] = [];

    /**
     * is this component used in small version
     *
     * @type {boolean}
     */
    @Input() smallPreview: boolean = false;

    error;

    constructor(
        public plt: PlatformService,
        public appEvents: Events,
        public conversationService: ConversationService,
        public overlayService: OverlayService,
        public modalController: ModalController,
        public alertCtrl: AlertController,
        public previewService: PreviewService
    ) {
        super();
        this.isPreview = true;
        this.hideHeader = true;

        // user language
        this.userLang = this.translate.currentLang;
    }

    ngOnInit() {
        window.addEventListener('message', (event) => {
            if (event.data && event.data.type == 'preview-attendee-detail') {
                this.translate.use(event.data.lang);

                // init event
                if (event.data.event) {
                    this.event = new Event(event.data.event);
                }

                // init attendee
                if (event.data.attendee) {
                    this.initParticipant(event.data.attendee);
                }

                // init user
                this.initUser(event.data.attendee.user || new User);

                this.loading = false;
            }
        });
    }

    /**
     * after view init
     *
     * @return void
     */
    ngAfterViewInit() {
        if (this.user && this.user.id) {
            this.refresh();
        }
    }

    /**
     * refresh page content
     *
     * @return void
     */
    public refresh($event?) {
        // this.dataLoading = true;

        window.parent.postMessage({
            type: 'do-preview-attendee-detail'
        }, '*');
    }

    private initUser(user: User) {
        user.selected_participant_id = this.attendee.id;
        user.selected_participant = this.attendee;
        user.selected_participant.event = this.event;

        this.user = new User(user);
    }

    private initParticipant(attendee: Participant) {
        this.attendee = new Participant(attendee);

        // display all info from form if it is preview
        this.attendee.groupedDetails.forEach((group) => {
            group.visible = true;
        });

        this.setupContainers();

        // load group icon if there is any...
        if (this.event.use_groups && this.event.use_groups_icons) {
            let group = this.event.groups.filter(g => g.id == this.attendee.group_id);
            if (group[0]) {
                this.group = group[0];
                this.groupIcon = group[0].icon;
            }
        }
    }

    /**
     * Show only containers where attendee has some keywords
     *
     */
    private setupContainers() {
        this.containers = [];

        for (let c = 0; c < this.event.containers.length; c++) {
            const container = new Container(this.event.containers[c]);
            this.event.containers[c] = container;
            let keywordIncluded = false;
            // keywords
            if (container.keywords) {
                for (let k = 0; k < container.keywords.length; k++) {
                    const keyword = container.keywords[k];

                    if (this.attendee.keywords.length
                        && this.attendee.keywords.find((item) => { return keyword.id == item.id })) {
                        keywordIncluded = true;
                    }
                }
            }

            // order keywords
            container.orderKeywordsByName(this.userLang, this.event.default_locale);

            // check if some keyword is checked, then include it in visible list
            if (keywordIncluded) {
                this.containers.push(container);
            }
        }
    }

    /**
     * Toggle like
     *
     * @param participant
     */
    public toggleLike(attendee: Participant) {
        return false;
    }

    /**
     * Toggle bookmark
     *
     * @param participant
     */
    public toggleBookmark(attendee: Participant) {
        return false;
    }

    /**
     * is keyword a match
     *
     * @param {Keyword} keyword
     * @param {Container} container
     * @param {number} column
     *
     * @returns {number}
     */
    public isMatch(keyword: Keyword, container: Container, column: number): boolean {
        return false;
    }

    /**
     * rediret to chat with selected attendee
     *
     */
    public goToMessages() {
        return false;
    }

    /**
     * close modal window
     *
     */
    public backClicked() {
        return false;
    }


    /**
     * close appointment request
     *
     * @param {Conversation} conversation
     *
     */
    public closeAppointmentRequest(conversation: Conversation) {
        return false;
    }

    /**
     * close modal window and redirect to dashboard with manage keywords
     *
     */
    manageKeywords() {
        return false;
    }

    public async openAnswerAppointment(appointment: Appointment, status: number) {
        return false;
    }

    public addCacnelReason(reason) {
        return false;
    }

    /**
     * answer appointment
     *
     * @param appointment
     * @param status
     *
     * @return void
     */
    public answerAppointment(appointment: Appointment, status: number) {
        return false;
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
